import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { z } from "zod";

type ServiceTypeDto = z.infer<typeof schemas.ServiceTypeDto>;
export type DrivingSchoolServiceType = ServiceTypeDto;

class DrivingSchoolServiceTypesProvider extends AbstractDataProvider<DrivingSchoolServiceType> {
  async getOne(
    _resource: string,
    { id }: GetOneParams<DrivingSchoolServiceType>,
  ): Promise<GetOneResult<DrivingSchoolServiceType>> {
    const serverClient = await getAuthenticatedServerClient();
    const serviceType = await serverClient.getServiceType({ params: { id } });
    return { data: serviceType };
  }

  async getList(
    _resource: string,
    { filter, sort, pagination }: GetListParams,
  ): Promise<GetListResult<DrivingSchoolServiceType>> {
    const { drivingSchoolId, ...clientSideFilter } = filter ?? {};
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const serverClient = await getAuthenticatedServerClient();
    const serviceTypes = await serverClient.getDrivingSchoolServiceTypes({ params: { id: drivingSchoolId } });
    return applyPagination(applySort(applyFilter(serviceTypes, clientSideFilter), sort), pagination);
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<DrivingSchoolServiceType>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("serviceTypes", { filter: { ...filter, drivingSchoolId: id }, sort, pagination });
  }

  async create(
    _resource: string,
    { data }: CreateParams<Partial<DrivingSchoolServiceType>>,
  ): Promise<CreateResult<DrivingSchoolServiceType>> {
    const payload = schemas.CreateServiceTypeDto.parse(data);
    const serverClient = await getAuthenticatedServerClient();
    const serviceType = await serverClient.createServiceType(payload);
    return { data: serviceType };
  }

  async update(
    _resource: string,
    { id, data }: UpdateParams<DrivingSchoolServiceType>,
  ): Promise<UpdateResult<DrivingSchoolServiceType>> {
    const payload = schemas.UpdateServiceTypeDto.parse(data);
    const serverClient = await getAuthenticatedServerClient();
    const serviceType = await serverClient.updateServiceType(payload, { params: { id } });
    return { data: serviceType };
  }

  async delete(_resource: string, { id }: DeleteParams<DrivingSchoolServiceType>): Promise<DeleteResult> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.softDeleteServiceType(undefined, { params: { id } });
    return { data: {} };
  }
}

export const drivingSchoolServiceTypesProvider = new DrivingSchoolServiceTypesProvider();
